import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { STATUS_LABEL, type InspectionRow } from "../../api/inspections";
import { StatusChip, type Tone } from "../../components/ui";

export const STATUS_TONE: Record<string, Tone> = { draft: "draft", submitted: "ok" };

/** The rail's rows: the works order, where it stands and when; the site and
 * the kind of work; and how many of its checks are answered across every
 * valve, so a half-done inspection stands out without opening it. */
export default function InspectionList({ rows, selected, search }: { rows: readonly InspectionRow[]; selected: number | null; search: string }) {
  return (
    <nav className="insp-list" aria-label="Inspection list">
      {rows.map((row) => (
        <InspectionListRow key={row.id} row={row} current={row.id === selected} to={`/inspections/${row.id}${search ? `?${search}` : ""}`} />
      ))}
    </nav>
  );
}

function InspectionListRow({ row, current, to }: { row: InspectionRow; current: boolean; to: string }) {
  const total = row.total_checks;
  const share = total === 0 ? 0 : Math.min(100, (row.answered / total) * 100);
  const where = row.site?.name ?? row.chamber_name ?? "No site yet";
  return (
    <Link to={to} className="insp-row" aria-current={current ? "page" : undefined}>
      <span className="insp-row-top">
        <span className="mono insp-row-wo">{row.work_order_no}</span>
        <StatusChip tone={STATUS_TONE[row.status] ?? "quiet"}>{STATUS_LABEL[row.status] ?? row.status}</StatusChip>
        <span className="insp-row-kind">{row.kind_label}</span>
        <span className="insp-row-date">{row.inspection_date ? formatDate(row.inspection_date) : ""}</span>
      </span>
      <span className="insp-row-site">
        {where}
        {row.work_type_label ? ` · ${row.work_type_label}` : ""}
      </span>
      <span className="insp-row-progress">
        <span className={share === 100 ? "meter full" : "meter"} aria-hidden="true">
          <span style={{ width: `${share}%` } as CSSProperties} />
        </span>
        <span aria-label={`${row.answered} of ${total} checks answered`}>
          {row.answered}/{total}
        </span>
      </span>
    </Link>
  );
}
